import { formatNumber, formatStorageSize, formatStorageUsage, formatStorageValue } from '@qse/utils'

interface UsageProgressBarProps {
  used: number
  limit: number
  percentage: number
  unit?: string
  className?: string
  allocated?: number
}

/**
 * Reusable progress bar component for displaying usage metrics
 */
const UsageProgressBar = ({
  used,
  limit,
  percentage,
  unit = '',
  className = '',
  allocated,
}: UsageProgressBarProps) => {
  // Detect over-quota: percentage >= 100 OR (limit is 0 and used > 0)
  const isOverQuota = percentage >= 100 || (limit === 0 && used > 0)
  const barColor = isOverQuota ? 'bg-red-600 dark:bg-red-500' : 'bg-blue-600 dark:bg-blue-400'

  // Use 100% width when over-quota due to zero quota
  const displayPercentage = isOverQuota && limit === 0 && used > 0 ? 100 : Math.min(percentage, 100)

  // Format the display values based on unit
  const formatValue = (value: number, showUnit = true) => {
    // For storage units (bytes or GB), use dynamic formatter
    if (unit === 'bytes') {
      // Values are in bytes, use dynamic formatter
      return formatStorageValue(value)
    }

    if (unit === 'GB') {
      // Values might already be in GB, but could also be bytes - use formatStorageSize for backward compatibility
      // If the value is very large (> 1 PB), it's likely bytes
      if (value > 1024 * 1024 * 1024 * 1024) {
        // Very large value, likely in bytes
        return formatStorageValue(value)
      }

      return formatStorageSize(value)
    }

    // For other units (like "calls/month"), format without unit if showUnit is false
    return showUnit ? `${formatNumber(value)}${unit}` : formatNumber(value)
  }

  // Format storage display using dynamic formatter when unit is bytes or GB
  const formatStorageDisplay = () => {
    if (unit === 'bytes' || unit === 'GB') {
      return formatStorageUsage(used, limit)
    }

    // For non-storage units, use old logic
    const showUnitOnce = unit && unit !== 'GB' && unit !== 'bytes'

    if (showUnitOnce) {
      return `${formatValue(used, false)} / ${formatValue(limit, false)}${unit}`
    }

    return `${formatValue(used)} / ${formatValue(limit)}`
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <span className="text-gray-900 dark:text-white text-sm">{formatStorageDisplay()}</span>
      {allocated !== undefined && (
        <span className="text-gray-900 dark:text-white text-sm">
          Allocated: {formatValue(allocated)}
        </span>
      )}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 relative">
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${barColor}`}
          style={{
            width: `${displayPercentage}%`,
          }}
        />
      </div>
      {/* Show over-quota message when quota is 0 */}
      {limit === 0 && used > 0 && (
        <span className="text-xs text-red-600 dark:text-red-400">
          Over quota - No quota allocated
        </span>
      )}
    </div>
  )
}

export default UsageProgressBar
